import React, { useEffect, useState } from 'react';
import { Box, TextField, Button, Typography, Card, CardContent, Grid, MenuItem, Alert, Stack, Divider } from '@mui/material';
import { useParams, useHistory } from 'react-router-dom';
import { IconHome2, IconMapPin, IconCoin, IconPhotoPlus, IconArrowLeft, IconFileDescription } from '@tabler/icons';
import api from '../../../../services/api';
import ImovelPriceInput from './ImovelPriceInput';
import ImovelPhotosEditor from './ImovelPhotosEditor';
import { STATUS_OPTIONS } from './CreateImovel';
import { sanitizeDecimalKeystroke, parseDecimalInput } from '../../../../../utils/numberInput';
import SectionHeader from '../../../../../ui-component/extended/FormSectionHeader';

const TIPO_OPTIONS = [
    { value: 'casa', label: 'Casa' },
    { value: 'apartamento', label: 'Apartamento' },
    { value: 'terreno', label: 'Terreno' },
    { value: 'comercial', label: 'Comercial' },
    { value: 'outro', label: 'Outro' }
];

const FINALIDADE_OPTIONS = [
    { value: 'venda', label: 'Venda' },
    { value: 'aluguel', label: 'Aluguel' }
];

const EditImovel = () => {
    const { id } = useParams();
    const history = useHistory();

    const [imovel, setImovel] = useState({});
    const [fields, setFields] = useState({});
    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [appId, setAppId] = useState(null);
    const [imagensExistentes, setImagensExistentes] = useState([]);
    const [imagensRemovidas, setImagensRemovidas] = useState([]);
    const [novaCapaId, setNovaCapaId] = useState(null);
    const [novasFotos, setNovasFotos] = useState([]);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadImovel();
        loadFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadImovel = async () => {
        const res = await api.get(`v1/apps/${appId}/imoveis/${id}/`);
        setImovel({ ...res.data, area_m2: res.data.area_m2 ?? '', area_util_m2: res.data.area_util_m2 ?? '' });
        setFields(res.data.custom_fields || {});
        setImagensExistentes(res.data.imagens || []);
    };

    const loadFields = async () => {
        const res = await api.get(`v1/apps/${appId}/imovel_fields/`);
        setFieldsConfig(res.data);
    };

    const removeExistingImage = (imageId) => {
        setImagensExistentes(imagensExistentes.filter((img) => img.id !== imageId));
        setImagensRemovidas([...imagensRemovidas, imageId]);
    };

    const setPrincipal = (imageId) => {
        setImagensExistentes(imagensExistentes.map((img) => ({ ...img, principal: img.id === imageId })));
        setNovaCapaId(imageId);
    };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...imovel,
                area_m2: parseDecimalInput(imovel.area_m2),
                area_util_m2: parseDecimalInput(imovel.area_util_m2)
            };

            const formData = new FormData();
            formData.append('imovel', JSON.stringify(payload));
            formData.append('fields', JSON.stringify(fields));
            novasFotos.forEach((file) => formData.append('imagens', file));
            if (imagensRemovidas.length > 0) formData.append('remove_image_ids', JSON.stringify(imagensRemovidas));
            if (novaCapaId) formData.append('principal_image_id', novaCapaId);

            await api.patch(`v1/apps/${appId}/imoveis/${id}/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            history.push(`/dashboard/config/imovel/${id}`);
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao salvar imóvel.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2.5, md: 5 }, maxWidth: 1000, mx: 'auto' }}>
            <Button
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => history.push(`/dashboard/config/imovel/${id}`)}
                sx={{ textTransform: 'none', mb: 1, color: 'text.secondary' }}
            >
                Voltar
            </Button>

            <Typography variant="h3" fontWeight="800" mb={0.5}>
                Editar Imóvel
            </Typography>
            <Typography color="text.secondary" mb={4}>
                {imovel.titulo}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            <Card sx={{ borderRadius: 4, mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 3.5 }}>
                    <SectionHeader icon={<IconHome2 size={20} />} title="Informações básicas" subtitle="Tipo, finalidade e características do imóvel" />

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Título"
                                fullWidth
                                required
                                value={imovel.titulo || ''}
                                onChange={(e) => setImovel({ ...imovel, titulo: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <TextField
                                select
                                label="Tipo"
                                fullWidth
                                value={imovel.tipo || 'casa'}
                                onChange={(e) => setImovel({ ...imovel, tipo: e.target.value })}
                            >
                                {TIPO_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <TextField
                                select
                                label="Finalidade"
                                fullWidth
                                value={imovel.finalidade || 'venda'}
                                onChange={(e) => setImovel({ ...imovel, finalidade: e.target.value })}
                            >
                                {FINALIDADE_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <TextField
                                select
                                label="Status"
                                fullWidth
                                value={imovel.status || 'disponivel'}
                                onChange={(e) => setImovel({ ...imovel, status: e.target.value })}
                            >
                                {STATUS_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Área (m²)"
                                fullWidth
                                placeholder="0,00"
                                value={imovel.area_m2}
                                onChange={(e) => setImovel({ ...imovel, area_m2: sanitizeDecimalKeystroke(e.target.value) })}
                            />
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Área útil (m²)"
                                fullWidth
                                placeholder="0,00"
                                value={imovel.area_util_m2}
                                onChange={(e) => setImovel({ ...imovel, area_util_m2: sanitizeDecimalKeystroke(e.target.value) })}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3.5 }} />

                    <SectionHeader icon={<IconMapPin size={20} />} title="Localização" subtitle="Onde o imóvel está localizado" />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Endereço"
                                fullWidth
                                value={imovel.endereco || ''}
                                onChange={(e) => setImovel({ ...imovel, endereco: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={6} sm={2}>
                            <TextField
                                label="Bairro"
                                fullWidth
                                value={imovel.bairro || ''}
                                onChange={(e) => setImovel({ ...imovel, bairro: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={6} sm={2}>
                            <TextField
                                label="Cidade"
                                fullWidth
                                value={imovel.cidade || ''}
                                onChange={(e) => setImovel({ ...imovel, cidade: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={6} sm={2}>
                            <TextField
                                label="Estado"
                                fullWidth
                                value={imovel.estado || ''}
                                onChange={(e) => setImovel({ ...imovel, estado: e.target.value })}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3.5 }} />

                    <SectionHeader icon={<IconCoin size={20} />} title="Preço" subtitle="Como o valor aparece pro cliente" />
                    <ImovelPriceInput
                        valor={imovel.valor}
                        valorAPartirDe={imovel.valor_a_partir_de}
                        onChange={({ valor, valorAPartirDe }) => setImovel({ ...imovel, valor, valor_a_partir_de: valorAPartirDe })}
                    />

                    <Divider sx={{ my: 3.5 }} />

                    <SectionHeader icon={<IconFileDescription size={20} />} title="Descrição" />
                    <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        value={imovel.descricao || ''}
                        onChange={(e) => setImovel({ ...imovel, descricao: e.target.value })}
                    />

                    <Divider sx={{ my: 3.5 }} />

                    <SectionHeader icon={<IconPhotoPlus size={20} />} title="Fotos" subtitle="Clique na estrela pra trocar a foto de capa" />
                    <ImovelPhotosEditor
                        appId={appId}
                        existingImages={imagensExistentes}
                        onRemoveExisting={removeExistingImage}
                        onSetPrincipal={setPrincipal}
                        newFiles={novasFotos}
                        onNewFilesChange={setNovasFotos}
                    />
                </CardContent>
            </Card>

            {fieldsConfig.length > 0 && (
                <Card sx={{ borderRadius: 4, mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ p: 3.5 }}>
                        <Typography fontWeight="700" mb={2}>
                            Campos adicionais
                        </Typography>

                        <Grid container spacing={2}>
                            {fieldsConfig.map((f) => (
                                <Grid item xs={6} key={f.id}>
                                    <TextField
                                        label={f.title}
                                        required={f.required}
                                        fullWidth
                                        value={fields[f.name] || ''}
                                        onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            <Stack direction="row" spacing={2}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={save}
                    disabled={saving}
                    sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, px: 4 }}
                >
                    {saving ? 'Salvando...' : 'Salvar alterações'}
                </Button>

                <Button
                    variant="outlined"
                    size="large"
                    onClick={() => history.push(`/dashboard/config/imovel/${id}`)}
                    sx={{ borderRadius: 2.5, textTransform: 'none' }}
                >
                    Cancelar
                </Button>
            </Stack>
        </Box>
    );
};

export default EditImovel;
